
import React, { useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from '@/context/AuthContext';
import { Button } from "@/components/ui/button";
import { Clock, FileText, CheckCircle2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

interface EmailTemplate {
  id: string;
  title: string;
  content: string;
  format: 'html' | 'jsx' | 'text';
  email_type: string;
  created_at: string;
}

interface TemplateHistoryProps {
  onSelect: (content: string) => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatTitle = (title: string) => {
  if (!title) return 'Untitled Template';
  return title.length > 24 ? title.substring(0, 24) + '...' : title;
};

const TemplateHistory: React.FC<TemplateHistoryProps> = ({ onSelect }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchTemplates();
  }, [user]);

  const fetchTemplates = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const validFormats = ['html', 'jsx', 'text'];
      
      const typedData = (data || []).map(item => ({
        ...item,
        format: validFormats.includes(item.format) ? item.format as 'html' | 'jsx' | 'text' : 'html'
      }));
      
      setTemplates(typedData as EmailTemplate[]);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: "Error",
        description: "Failed to load template history",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTemplate = (template: EmailTemplate) => {
    setSelectedTemplateId(template.id);
    onSelect(template.content);
    
    toast({
      title: "Template loaded",
      description: "The selected template has been loaded to the preview",
    });
  };

  const getFormatBadge = (format: string) => {
    switch (format) {
      case 'html':
        return <Badge className="bg-blue-500 hover:bg-blue-600">HTML</Badge>;
      case 'jsx':
        return <Badge className="bg-purple-500 hover:bg-purple-600">JSX</Badge>;
      case 'text':
        return <Badge className="bg-gray-500 hover:bg-gray-600">Text</Badge>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-white">Previous Templates</h3>
          <Button variant="outline" size="sm" className="border-gray-700 text-gray-300">
            <Clock className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
        {Array(5).fill(0).map((_, i) => (
          <div key={i} className="p-3 border border-gray-800 rounded-lg bg-gray-800/50">
            <div className="flex justify-between items-center mb-2">
              <Skeleton className="h-4 w-3/4 bg-gray-700" />
              <Skeleton className="h-4 w-1/4 bg-gray-700" />
            </div>
            <Skeleton className="h-3 w-1/2 bg-gray-700" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-white">Previous Templates</h3>
        <Button 
          variant="outline" 
          size="sm" 
          className="border-gray-700 text-gray-300 hover:bg-gray-700" 
          onClick={fetchTemplates}
        >
          <Clock className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>
      
      <ScrollArea className="h-96 pr-4">
        <div className="space-y-2">
          {templates.length === 0 ? (
            <div className="text-center p-6 border border-dashed border-gray-700 rounded-lg">
              <FileText className="h-12 w-12 mx-auto text-gray-500 mb-2" />
              <p className="text-gray-400">No templates found</p>
              <p className="text-xs text-gray-500 mt-1">Generate your first email to see it here</p>
            </div>
          ) : (
            templates.map((template) => (
              <div 
                key={template.id} 
                className={`p-3 border ${selectedTemplateId === template.id ? 'border-jade bg-jade/10' : 'border-gray-700 bg-gray-800/50'} rounded-lg hover:bg-gray-700/50 transition-all cursor-pointer group`}
                onClick={() => handleSelectTemplate(template)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-200 text-sm">{formatTitle(template.title)}</h4>
                      {selectedTemplateId === template.id && (
                        <CheckCircle2 className="h-4 w-4 text-jade" />
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(template.created_at)}</p>
                  </div>
                  <div className="flex-shrink-0">
                    {getFormatBadge(template.format)}
                  </div>
                </div>
                <div className="flex justify-between mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-xs text-jade hover:text-white hover:bg-jade p-1 h-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectTemplate(template);
                    }}
                  >
                    Load template
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default TemplateHistory;
